import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CEFR_LEVELS, COMMON_LANGUAGES } from "@/types/candidate";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";

export function LanguagesPanel() {
	const { t } = useTranslation();
	const { draft, updateDraft } = usePeopleSearch();
	const items = draft.languages.items;

	const addLanguage = (code: string) => {
		if (items.some(item => item.code === code))
			return;
		const label = COMMON_LANGUAGES[code as keyof typeof COMMON_LANGUAGES] || code;
		updateDraft('languages', { items: [...items, { code, label, minCefr: null }] });
	};

	const updateMinCefr = (code: string, minCefr: number | null) => {
		updateDraft('languages', {
			items: items.map(item => item.code === code ? { ...item, minCefr } : item),
		});
	};

	const removeLanguage = (code: string) => {
		updateDraft('languages', { items: items.filter(item => item.code !== code) });
	};

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				{t("peopleSearch.languages.hint", "Candidates speaking any of these languages")}
			</p>
			<Select value="" onValueChange={addLanguage}>
				<SelectTrigger>
					<SelectValue placeholder={t("peopleSearch.languages.addPlaceholder", "Add a language")} />
				</SelectTrigger>
				<SelectContent>
					{Object.entries(COMMON_LANGUAGES)
						.filter(([code]) => !items.some(item => item.code === code))
						.map(([code, label]) => (
							<SelectItem key={code} value={code}>{label}</SelectItem>
						))}
				</SelectContent>
			</Select>
			<div className="space-y-2">
				{items.map(item => (
					<div key={item.code} className="flex items-center gap-2 border rounded-sm p-2">
						<span className="text-sm flex-1">{item.label}</span>
						<Select
							value={item.minCefr === null ? 'any' : String(item.minCefr)}
							onValueChange={v => updateMinCefr(item.code, v === 'any' ? null : Number(v))}
						>
							<SelectTrigger className="w-[210px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="any">{t("peopleSearch.languages.anyLevel", "Any level")}</SelectItem>
								{Object.entries(CEFR_LEVELS)
									.filter(([level]) => level !== '0')
									.map(([level, label]) => (
										<SelectItem key={level} value={level}>≥ {label}</SelectItem>
									))}
							</SelectContent>
						</Select>
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0"
							onClick={() => removeLanguage(item.code)}
							aria-label={t("peopleSearch.languages.remove", "Remove language")}
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				))}
			</div>
		</div>
	);
}
